import logger from '@/utils/logger';
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../components/auth/AuthProvider';
import { Eye, EyeOff, Mail, Lock, User, Chrome } from 'lucide-react';
import { getStudioUrl } from '../utils/domain';
import CreationSpark from '../components/brand/CreationSpark';
import Logo from '../components/brand/Logo';

export default function Login() {
 // ========== MODE BETA (TEMPORAIRE) ==========
 // METTRE À false POUR DÉSACTIVER LA WHITELIST APRÈS LA BETA
 const BETA_MODE = true;
 // ============================================
 
 const [isSignUp, setIsSignUp] = useState(false);
 const [email, setEmail] = useState('');
 const [password, setPassword] = useState('');
 const [fullName, setFullName] = useState('');
 const [showPassword, setShowPassword] = useState(false);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');
 const [passwordStrength, setPasswordStrength] = useState<{
 hasLower: boolean;
 hasUpper: boolean;
 hasDigit: boolean;
 hasSymbol: boolean;
 minLength: boolean;
 }>({ hasLower: false, hasUpper: false, hasDigit: false, hasSymbol: false, minLength: false });

 const { user, loading: authLoading, signIn, signUp, signInWithGoogle } = useAuth();
 const navigate = useNavigate();

 // PROTECTION: Si déjà connecté, rediriger vers studio
 useEffect(() => {
 logger.log(' [Login Auth Check]', {
 authLoading,
 user: user ? `${user.email} (${user.id})` : 'null',
 willRedirect: !authLoading && user
 });

 if (!authLoading && user) {
 const studioUrl = getStudioUrl();
 logger.log(' [Login] REDIRECT to studio (already logged in)', {
 studioUrl,
 isAbsolute: studioUrl.startsWith('http')
 });
 
 if (studioUrl.startsWith('http')) {
 window.location.href = studioUrl;
 } else {
 navigate(studioUrl);
 }
 }
 }, [authLoading, user, navigate]);

 // Validation du mot de passe selon les règles Supabase
 const validatePassword = (pwd: string) => {
 const strength = {
 hasLower: /[a-z]/.test(pwd),
 hasUpper: /[A-Z]/.test(pwd),
 hasDigit: /[0-9]/.test(pwd),
 hasSymbol: /[^a-zA-Z0-9]/.test(pwd),
 minLength: pwd.length >= 8
 };
 setPasswordStrength(strength);
 return Object.values(strength).every(v => v);
 };

 const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 const pwd = e.target.value;
 setPassword(pwd);
 if (isSignUp) {
 validatePassword(pwd);
 }
 };

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 setLoading(true);
 setError('');

 // Validation du mot de passe pour l'inscription
 if (isSignUp && !validatePassword(password)) {
 setError('Le mot de passe ne respecte pas les critères de sécurité requis');
 setLoading(false);
 return;
 }

 try {
 if (isSignUp) {
 const { error } = await signUp(email, password, { 
 full_name: fullName,
 data: { display_name: fullName }
 });
 if (error) throw error;
 
 // Après inscription réussie, redirection intelligente
 const studioUrl = getStudioUrl();
 if (studioUrl.startsWith('http')) {
 window.location.href = studioUrl;
 } else {
 navigate(studioUrl);
 }
 } else {
 const { error } = await signIn(email, password);
 if (error) throw error;
 
 // Après connexion réussie, redirection intelligente
 const studioUrl = getStudioUrl();
 if (studioUrl.startsWith('http')) {
 window.location.href = studioUrl;
 } else {
 navigate(studioUrl);
 }
 }
 } catch (error: any) {
 // Messages d'erreur en français
 const errorMessages: Record<string, string> = {
 'Invalid login credentials': 'Email ou mot de passe incorrect',
 'Email not confirmed': 'Veuillez confirmer votre email avant de vous connecter',
 'User already registered': 'Un compte existe déjà avec cet email',
 'Password should be at least 8 characters': 'Le mot de passe doit contenir au moins 8 caractères',
 'Signup requires a valid password': 'Mot de passe invalide',
 };
 
 setError(errorMessages[error.message] || error.message);
 } finally {
 setLoading(false);
 }
 };

 const handleGoogleSignIn = async () => {
 setLoading(true);
 setError('');
 
 try {
 const { error } = await signInWithGoogle();
 if (error) throw error;
 } catch (error: any) {
 setError('Erreur lors de la connexion avec Google. Veuillez réessayer.');
 } finally {
 setLoading(false);
 }
 };

 return (
 <div className="min-h-screen bg-theme-base grid lg:grid-cols-2">
 <div className="hidden lg:block login-stage relative min-h-screen">
 <CreationSpark variant="hero" />
 <div className="login-stage-veil">
 <Link to="/" className="flex items-center">
 <Logo wordmark inverse />
 </Link>
 <div>
 <p className="page-kicker mb-3">Clarifier</p>
 <p className="font-display text-4xl font-normal text-white leading-tight max-w-sm">
 Vos données,<br /><em>enfin claires.</em>
 </p>
 </div>
 <p className="text-sm text-white/55 max-w-sm">
 Données hébergées en France. Mémoire persistante. Analyse sans spectacle.
 </p>
 </div>
 </div>

 <div className="flex items-center justify-center p-6 sm:p-10">
 <div className="w-full max-w-md">
 <div className="mb-10 lg:hidden">
 <Link to="/" className="flex items-center mb-8">
 <Logo wordmark />
 </Link>
 </div>
 <p className="page-kicker mb-3">{isSignUp ? 'Inscription' : 'Accès'}</p>
 <h1 className="font-display text-3xl font-medium text-theme-primary mb-2">
 {isSignUp ? 'Créer un compte' : 'Connexion'}
 </h1>
 <p className="text-theme-secondary text-sm mb-8">
 {isSignUp 
 ? 'Commencez à analyser vos données.' 
 : 'Entrez dans le studio.'
 }
 </p>

 <div className="border border-theme bg-theme-surface p-8">
 {/* Bouton Google (masqué en mode beta) */}
 {!BETA_MODE && (
 <>
 <Button
 type="button"
 variant="outline"
 className="w-full mb-6 flex items-center justify-center gap-3 py-6"
 onClick={handleGoogleSignIn}
 disabled={loading}
 >
 <Chrome className="w-5 h-5" />
 <span>Continuer avec Google</span>
 </Button>

 {/* Séparateur */}
 <div className="relative my-6">
 <div className="absolute inset-0 flex items-center">
 <div className="w-full border-t border-theme-border"></div>
 </div>
 <div className="relative flex justify-center text-sm">
 <span className="px-4 bg-theme-surface text-theme-secondary">ou par email</span>
 </div>
 </div>
 </>
 )}

 {/* Formulaire */}
 {isSignUp && BETA_MODE ? (
 // ========== MODE BETA: Redirection vers waitlist ==========
 <div className="text-center space-y-6">
 <div className="bg-theme-hover border border-theme p-6">
 <h3 className="font-display text-xl mb-3">
 Beta en cours
 </h3>
 <p className="text-theme-secondary mb-5 text-sm">
 Inscriptions temporairement sur invitation uniquement. Rejoignez notre liste d'attente pour tester Clairify en avant-première.
 </p>
 <div className="text-base text-gray-900 dark:text-white space-y-3 font-medium">
 <div className="flex items-center justify-center gap-3">
 <span className="text-2xl"></span>
 <span>500 000 tokens offerts</span>
 </div>
 <div className="flex items-center justify-center gap-3">
 <span className="text-2xl"></span>
 <span>Accès anticipé à toutes les features</span>
 </div>
 </div>
 </div>
 <Button
 type="button"
 onClick={() => navigate('/waitlist')}
 className="w-full font-semibold py-3 px-6 text-lg"
 >
 Demander un accès Beta →
 </Button>
 </div>
 ) : (
 // ========== FORMULAIRE NORMAL ==========
 <form onSubmit={handleSubmit} className="space-y-4">
 {/* Nom complet (inscription uniquement) */}
 {isSignUp && (
 <div>
 <label className="block text-sm font-medium text-theme-primary mb-2">
 <User className="w-4 h-4 inline mr-2" />
 Nom complet
 </label>
 <input
 type="text"
 value={fullName}
 onChange={(e) => setFullName(e.target.value)}
 className="w-full px-4 py-3 bg-theme-base border border-theme text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-theme-text-primary"
 placeholder="Jean Dupont"
 required={isSignUp}
 />
 </div>
 )}

 {/* Email */}
 <div>
 <label className="block text-sm font-medium text-theme-primary mb-2">
 <Mail className="w-4 h-4 inline mr-2" />
 Adresse email
 </label>
 <input
 type="email"
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 className="w-full px-4 py-3 bg-theme-base border border-theme text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-theme-text-primary"
 placeholder="vous@exemple.com"
 required
 />
 </div>

 {/* Mot de passe */}
 <div>
 <label className="block text-sm font-medium text-theme-primary mb-2">
 <Lock className="w-4 h-4 inline mr-2" />
 Mot de passe
 </label>
 <div className="relative">
 <input
 type={showPassword ? 'text' : 'password'}
 value={password}
 onChange={handlePasswordChange}
 className="w-full px-4 py-3 pr-12 bg-theme-base border border-theme text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-theme-text-primary"
 placeholder="••••••••"
 required
 minLength={8}
 />
 <button
 type="button"
 onClick={() => setShowPassword(!showPassword)}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-theme-tertiary hover:text-theme-primary transition-colors"
 >
 {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
 </button>
 </div>

 {/* Indicateurs de force du mot de passe (inscription uniquement) */}
 {isSignUp && password.length > 0 && (
 <div className="mt-3 p-3 bg-theme-base rounded-lg border border-theme-border">
 <p className="text-xs font-medium text-theme-secondary mb-2">Le mot de passe doit contenir :</p>
 <div className="space-y-1">
 <PasswordRequirement met={passwordStrength.minLength} text="Au moins 8 caractères" />
 <PasswordRequirement met={passwordStrength.hasLower} text="Une lettre minuscule (a-z)" />
 <PasswordRequirement met={passwordStrength.hasUpper} text="Une lettre majuscule (A-Z)" />
 <PasswordRequirement met={passwordStrength.hasDigit} text="Un chiffre (0-9)" />
 <PasswordRequirement met={passwordStrength.hasSymbol} text="Un caractère spécial (!@#$...)" />
 </div>
 </div>
 )}
 </div>

 {/* Message d'erreur */}
 {error && (
 <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
 <p className="text-sm text-red-400">{error}</p>
 </div>
 )}

 {/* Bouton de soumission */}
 <Button
 type="submit"
 className="w-full py-6 text-base"
 disabled={loading || (isSignUp && !Object.values(passwordStrength).every(v => v))}
 >
 {loading ? (
 <div className="flex items-center justify-center gap-2">
 <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
 <span>Chargement...</span>
 </div>
 ) : (
 isSignUp ? 'Créer mon compte' : 'Se connecter'
 )}
 </Button>
 </form>
 )}
 {/* ========== FIN MODE BETA ========== */}

 {/* Lien pour basculer entre connexion et inscription */}
 <div className="mt-6 text-center">
 <button
 onClick={() => {
 setIsSignUp(!isSignUp);
 setError('');
 setPassword('');
 }}
 className="text-sm text-theme-secondary hover:text-theme-primary"
 >
 {isSignUp 
 ? 'Déjà un compte ? Se connecter' 
 : 'Pas encore de compte ? S\'inscrire'
 }
 </button>
 </div>

 </div>

 {/* Retour à l'accueil */}
 <div className="mt-6 text-center">
 <Link 
 to="/" 
 className="text-sm text-theme-secondary hover:text-theme-primary transition-colors"
 >
 ← Retour à l'accueil
 </Link>
 </div>
 </div>
 </div>
 </div>
 );
}

// Composant pour afficher les exigences du mot de passe
function PasswordRequirement({ met, text }: { met: boolean; text: string }) {
 return (
 <div className="flex items-center gap-2 text-xs">
 <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
 met ? 'bg-green-500/20 text-green-400' : 'bg-theme-border text-theme-tertiary'
 }`}>
 {met && ''}
 </div>
 <span className={met ? 'text-theme-primary' : 'text-theme-tertiary'}>{text}</span>
 </div>
 );
}

